import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, UserPlus, DollarSign, Wallet, Briefcase, X, Zap } from "lucide-react";

const ACTIONS = [
  { id: "lead", label: "New Lead", to: "/pipeline", icon: UserPlus, hint: "Start a deal" },
  { id: "expense", label: "Log Expense", to: "/finance", icon: Wallet, hint: "Track a cost" },
  { id: "revenue", label: "Log Revenue", to: "/finance", icon: DollarSign, hint: "Record income" },
  { id: "client", label: "New Client", to: "/clients", icon: Briefcase, hint: "Add a company" },
];

export default function QuickActions() {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  // Keyboard shortcut: Cmd/Ctrl + .
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === ".") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handle = (a) => {
    setOpen(false);
    // Tell the destination page to auto-open the create dialog
    sessionStorage.setItem("equinox_quick_action", a.id);
    nav(a.to);
  };

  return (
    <>
      <button
        data-testid="quick-actions-btn"
        onClick={() => setOpen(true)}
        title="Quick Actions (⌘.)"
        className="fixed bottom-8 right-8 z-40 w-14 h-14 rounded-full bg-[#007AFF] hover:bg-[#0066CC] text-white flex items-center justify-center shadow-[0_8px_32px_rgba(0,122,255,0.55)] hover:shadow-[0_8px_40px_rgba(0,122,255,0.75)] transition-all hover:-translate-y-0.5"
      >
        <Zap className="w-5 h-5" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(8px)" }}
          onClick={() => setOpen(false)}
          data-testid="quick-actions-overlay"
        >
          <div
            className="relative w-full max-w-md rounded-3xl overflow-hidden"
            style={{
              background: "rgba(15,15,18,0.96)",
              border: "1px solid rgba(255,255,255,0.12)",
              backdropFilter: "blur(28px)",
              boxShadow: "0 28px 80px rgba(0,0,0,0.8)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-2xl bg-[#007AFF] flex items-center justify-center glow-blue">
                  <Zap className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#007AFF]/80">Quick Actions</div>
                  <div className="font-display text-lg">What do you want to do?</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/55"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-3">
              {ACTIONS.map((a) => {
                const Icon = a.icon;
                return (
                  <button
                    key={a.id}
                    onClick={() => handle(a)}
                    className="w-full flex items-center gap-4 px-4 py-3 rounded-2xl hover:bg-white/[0.05] transition group"
                    data-testid={`quick-action-${a.id}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-white/[0.05] group-hover:bg-[#007AFF]/15 flex items-center justify-center transition">
                      <Icon className="w-4 h-4 text-[#007AFF]" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm text-white font-medium">{a.label}</div>
                      <div className="text-[11px] text-white/40">{a.hint}</div>
                    </div>
                    <Plus className="w-3.5 h-3.5 text-white/30 group-hover:text-white/70 transition" />
                  </button>
                );
              })}
            </div>
            <div className="px-6 py-3 border-t border-white/[0.06] text-[10px] text-white/30 uppercase tracking-[0.25em] text-center">
              ⌘ + . to toggle
            </div>
          </div>
        </div>
      )}
    </>
  );
}
